import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../../services/api';
import './LabResults.css';

// ============================================================
// HEALTHSTORM WOMEN'S-OPTIMIZED RANGES
// ============================================================

const OPTIMAL_RANGES = {
  // HORMONES
  'TSH':                { min: 1, max: 2, concern_high: 4, concern_low: 0.5, category: 'hormones' },
  'Free T4':            { min: 1.1, max: 1.8, category: 'hormones' },
  'Free T3':            { min: 3.2, max: 4.2, concern_high: 5, concern_low: 2, category: 'hormones' },
  'Estradiol':          { category: 'hormones' },
  'Progesterone':       { category: 'hormones' },
  'LH':                 { category: 'hormones' },
  'FSH':                { category: 'hormones' },
  'AMH':                { category: 'hormones' },
  'Testosterone Total': { category: 'hormones' },
  'Testosterone Free':  { category: 'hormones' },
  'SHBG':               { category: 'hormones' },
  'DHEA-S':             { category: 'hormones' },
  'Inhibin B':          { category: 'hormones' },

  // GLUCOSE HANDLING
  'HbA1c':            { max: 5.3, concern_high: 6, category: 'glucose' },
  'Fasting Glucose':  { max: 89, concern_high: 126, category: 'glucose' },
  'Fasting Insulin':  { max: 5, concern_high: 20, category: 'glucose' },
  'HOMA-IR':          { max: 1.5, concern_high: 2.5, category: 'glucose' },
  'Uric Acid':        { min: 2.5, max: 6, concern_high: 7.1, category: 'glucose' },
  'Triglycerides':    { max: 50, concern_high: 200, category: 'glucose' },

  // INFLAMMATION
  'hsCRP':        { max: 0.3, concern_high: 8, category: 'inflammation' },
  'IL-6':         { max: 5, concern_high: 7, category: 'inflammation' },
  'MPO':          { max: 469, concern_high: 539, category: 'inflammation' },
  'Lp-PLA2':     { max: 180, concern_high: 225, category: 'inflammation' },
  'OxLDL':        { max: 1.3, concern_high: 1.6, category: 'inflammation' },
  'Homocysteine': { max: 7, concern_high: 14, category: 'inflammation' },

  // LIPIDS
  'Total Cholesterol': { min: 120, max: 175, concern_high: 275, category: 'lipids' },
  'LDL-C':             { max: 70, concern_high: 150, category: 'lipids' },
  'HDL-C':             { min: 60, concern_low: 40, category: 'lipids' },
  'TG:HDL Ratio':      { max: 2, category: 'lipids' },
  'ApoB':              { max: 62, concern_high: 120, category: 'lipids' },
  'Lp(a)':             { max: 30, concern_high: 125, category: 'lipids' },
  'ApoA1':             { min: 180, category: 'lipids' },
  'ApoB:ApoA1 Ratio':  { max: 0.6, category: 'lipids' },
  'VLDL':              { max: 30, concern_high: 40, category: 'lipids' },
  'Cholesterol/HDL Ratio': { max: 3, concern_high: 5, category: 'lipids' },
  'LDL/HDL Ratio':     { max: 2, concern_high: 3.5, category: 'lipids' },
  'LDL-P':             { category: 'lipids' },
  'HDL-P':             { category: 'lipids' },
  'Small Dense LDL':   { category: 'lipids' },

  // LIVER & KIDNEYS
  'ALT':            { max: 20, concern_high: 44, category: 'liver_kidney' },
  'AST':            { max: 20, concern_high: 70, category: 'liver_kidney' },
  'GGT':            { max: 10, concern_high: 30, category: 'liver_kidney' },
  'ALP':            { max: 40, concern_high: 80, category: 'liver_kidney' },
  'Bilirubin Total':{ max: 0.6, concern_high: 1.2, category: 'liver_kidney' },
  'Creatinine':     { min: 0.5, max: 0.8, concern_high: 1.2, category: 'liver_kidney' },
  'Cystatin C':     { min: 0.43, max: 0.7, concern_high: 0.95, category: 'liver_kidney' },
  'eGFR':           { min: 100, concern_low: 60, category: 'liver_kidney' },
  'BUN':            { category: 'liver_kidney' },
  'Albumin':        { category: 'liver_kidney' },

  // CBC — RED BLOOD CELLS
  'RBC':          { min: 4.1, max: 5.1, concern_low: 3.8, category: 'cbc' },
  'Hemoglobin':   { min: 13.3, max: 15.5, concern_low: 8, category: 'cbc' },
  'Hematocrit':   { min: 40, max: 46.5, concern_low: 24, category: 'cbc' },
  'MCV':          { min: 85, max: 95, concern_low: 77, concern_high: 103, category: 'cbc' },
  'MCH':          { min: 28.5, max: 31.5, concern_low: 10, concern_high: 33, category: 'cbc' },
  'MCHC':         { min: 32, max: 36, category: 'cbc' },
  'RDW':          { max: 12.5, concern_high: 15, category: 'cbc' },
  'Platelets':    { min: 200, max: 400, concern_low: 25, concern_high: 700, category: 'cbc' },

  // CBC — WHITE BLOOD CELLS / IMMUNE
  'WBC':              { min: 4000, max: 7500, concern_low: 3000, concern_high: 9500, category: 'cbc' },
  'Neutrophils':      { min: 3000, max: 5000, concern_low: 500, concern_high: 8000, category: 'cbc' },
  'Neutrophils (%)':  { min: 40, max: 48, concern_low: 30, concern_high: 75, category: 'cbc' },
  'Lymphocytes':      { min: 1100, max: 3100, concern_low: 500, concern_high: 3700, category: 'cbc' },
  'Lymphocytes (%)':  { min: 20, max: 40, category: 'cbc' },
  'Monocytes':        { min: 100, max: 900, category: 'cbc' },
  'Monocytes (%)':    { min: 2, max: 10, category: 'cbc' },
  'Eosinophils':      { min: 50, max: 250, concern_high: 1500, category: 'cbc' },
  'Eosinophils (%)':  { max: 2, concern_high: 8, category: 'cbc' },
  'Basophils':        { min: 0, max: 50, concern_high: 300, category: 'cbc' },
  'Basophils (%)':    { min: 0.1, max: 1.5, concern_high: 2, category: 'cbc' },
  'Immature Granulocyte':      { max: 0.1, category: 'cbc' },
  'Immature Granulocyte (%)':  { max: 1, concern_high: 6, category: 'cbc' },

  // NUTRIENTS
  'Ferritin':         { min: 40, max: 150, concern_low: 30, concern_high: 300, category: 'nutrients' },
  'Vitamin D':        { min: 40, max: 70, concern_low: 20, concern_high: 100, category: 'nutrients' },
  'Vitamin B12':      { min: 700, max: 1100, concern_low: 300, category: 'nutrients' },
  'Omega-3 Index':    { min: 10, category: 'nutrients' },
  'Folate':           { min: 15, concern_low: 5, category: 'nutrients' },
  'RBC Magnesium':    { min: 3.7, max: 7.0, category: 'nutrients' },
  'Magnesium':        { min: 1.7, max: 4.8, concern_low: 0.8, category: 'nutrients' },
  'Copper':           { min: 69, max: 132, category: 'nutrients' },
  'Zinc':             { min: 96, max: 115, concern_low: 75, category: 'nutrients' },
  'Selenium':         { min: 93, max: 198, category: 'nutrients' },
  'Molybdenum':       { min: 0.06, max: 0.14, category: 'nutrients' },
  'Iron':             { min: 50, max: 100, concern_low: 30, concern_high: 150, category: 'nutrients' },
  'TIBC':             { min: 240, max: 350, concern_high: 450, category: 'nutrients' },
  'Iron Saturation':  { category: 'nutrients' },
  'Calcium':          { min: 9, max: 9.8, concern_low: 8.6, concern_high: 11, category: 'nutrients' },
  'Sodium':           { min: 135, max: 145, concern_low: 130, concern_high: 150, category: 'nutrients' },
  'Potassium':        { min: 4, max: 4.4, concern_low: 2.5, concern_high: 6, category: 'nutrients' },
  'Chloride':         { min: 101, max: 106, concern_low: 97, concern_high: 110, category: 'nutrients' },
  'CO2':              { min: 24, max: 28, concern_low: 16, category: 'nutrients' },
  'Phosphorus':       { min: 2.5, max: 2.9, concern_low: 2, concern_high: 4.5, category: 'nutrients' },

  // GENETIC / HEAVY METALS
  'MTHFR':   { category: 'genetic_metals' },
  'Mercury':  { category: 'genetic_metals' },
  'Arsenic':  { category: 'genetic_metals' },
  'Lead':     { category: 'genetic_metals' },
};

// ============================================================
// LIFE-STAGE-DEPENDENT HORMONE RANGES
// ============================================================

const HORMONE_RANGES = {
  'Estradiol': {
    follicular:     { min: 20, max: 350 },
    midcycle:       { min: 150, max: 750 },
    luteal:         { min: 30, max: 450 },
    postmenopausal: { max: 20 },
  },
  'FSH': {
    follicular:     { min: 2.5, max: 10.2 },
    midcycle:       { min: 3.4, max: 33.4 },
    luteal:         { min: 1.5, max: 9.1 },
    postmenopausal: { min: 23, max: 116.3 },
  },
  'LH': {
    follicular:     { min: 0.1, max: 63.85 },
    midcycle:       { min: 14, max: 95.6 },
    luteal:         { min: 1, max: 11.4 },
    postmenopausal: { min: 7.7, max: 58.5 },
  },
  'Progesterone': {
    follicular:     { max: 1 },
    midcycle:       { min: 0.12, max: 12 },
    luteal:         { min: 5 },
    postmenopausal: { max: 0.2 },
  },
  'AMH': {
    age_20s: { min: 4.2, max: 4.7 },
    age_30s: { min: 1.69, max: 3.0 },
    age_40s: { min: 0.03, max: 0.58 },
  },
};

function getHormoneRange(testName, userContext) {
  const ranges = HORMONE_RANGES[testName];
  if (!ranges || !userContext) return null;

  if (testName === 'AMH') {
    const { age } = userContext;
    if (age == null) return null;
    if (age >= 20 && age < 30) return { ...ranges.age_20s, category: 'hormones' };
    if (age >= 30 && age < 40) return { ...ranges.age_30s, category: 'hormones' };
    if (age >= 40 && age < 50) return { ...ranges.age_40s, category: 'hormones' };
    return null;
  }

  const { menopauseStatus, cyclePhase } = userContext;
  if (menopauseStatus === 'postmenopausal' || menopauseStatus === 'perimenopausal') {
    return ranges.postmenopausal ? { ...ranges.postmenopausal, category: 'hormones' } : null;
  }
  if (menopauseStatus === 'premenopausal' && cyclePhase && ranges[cyclePhase]) {
    return { ...ranges[cyclePhase], category: 'hormones' };
  }
  return null;
}

const CATEGORY_INFO = {
  hormones: {
    name: 'Hormones',
    context: 'Estrogen increases thyroid binding globulin (TBG). Hashimoto\'s is 5\u20138x more common in women, peaking around perimenopause. Reproductive hormone levels vary significantly by menstrual cycle phase and menopausal status.',
  },
  glucose: {
    name: 'Glucose Handling',
    context: 'Estrogen enhances insulin sensitivity. When estrogen falls during menopause, insulin sensitivity drops ~20\u201330%, visceral fat rises, and diabetes risk increases.',
  },
  inflammation: {
    name: 'Inflammation',
    context: 'Women have 30\u201350% higher baseline CRP than men at every BMI. Estrogen is protective; with menopause nearly all inflammatory markers rise. Women have higher autoimmune incidence.',
  },
  lipids: {
    name: 'Lipids',
    context: 'LDL increases 10\u201320% post-menopause. Estrogen raises HDL quantity AND quality. Lipid changes are among the earliest detectable shifts during perimenopause.',
  },
  liver_kidney: {
    name: 'Liver & Kidneys',
    context: 'Estrogen protects liver metabolism, resulting in lower "normal" ALT/AST/GGT for women. Lower muscle mass means creatinine ranges should be lower; Cystatin C\u2013based eGFR is more accurate in women.',
  },
  cbc: {
    name: 'Blood Counts (CBC)',
    context: 'Women typically have lower RBC/hemoglobin/hematocrit than men due to menstruation; these rise after menopause. Platelets are slightly higher. Interpret with attention to life stage.',
  },
  nutrients: {
    name: 'Nutrients',
    context: 'Women are at high risk of iron deficiency (menstruation, pregnancy). Estrogen increases copper. Selenium is critical for thyroid and fertility. Optimizing nutrients \u2014 not just reaching "normal" \u2014 is especially beneficial for women.',
  },
  genetic_metals: {
    name: 'Genetic & Heavy Metals',
    context: 'MTHFR variants have greater clinical importance for women\'s reproductive and vascular health. Women absorb and metabolize heavy metals differently. Pregnancy and menopause may increase circulating levels.',
  },
};

const CATEGORY_ORDER = [
  'hormones', 'glucose', 'inflammation', 'lipids',
  'liver_kidney', 'cbc', 'nutrients', 'genetic_metals',
];

// ============================================================
// SCORING
// ============================================================

function scoreTest(testName, value, userContext = null) {
  let range = OPTIMAL_RANGES[testName];
  if (HORMONE_RANGES[testName]) {
    const resolved = getHormoneRange(testName, userContext);
    if (!resolved) return null;
    range = resolved;
  }
  if (!range || value === null || value === undefined || isNaN(value)) return null;
  const { min, max, concern_high, concern_low } = range;
  if (min === undefined && max === undefined) return null;

  const aboveMin = min === undefined || value >= min;
  const belowMax = max === undefined || value <= max;
  if (aboveMin && belowMax) return 95;

  if (concern_high !== undefined && value >= concern_high) return 25;
  if (concern_low !== undefined && value <= concern_low) return 25;

  if (max !== undefined && value > max) {
    if (concern_high !== undefined) {
      const ratio = Math.min((value - max) / (concern_high - max), 1);
      return Math.round(95 - ratio * 70);
    }
    return 60;
  }
  if (min !== undefined && value < min) {
    if (concern_low !== undefined) {
      const ratio = Math.min((min - value) / (min - concern_low), 1);
      return Math.round(95 - ratio * 70);
    }
    return 60;
  }
  return 60;
}

function getStatus(score) {
  if (score === null) return 'unknown';
  if (score >= 80) return 'optimal';
  if (score >= 50) return 'borderline';
  return 'concerning';
}

function getOptimalDisplay(testName, userContext = null) {
  let r = OPTIMAL_RANGES[testName];
  if (HORMONE_RANGES[testName]) {
    const resolved = getHormoneRange(testName, userContext);
    if (resolved) r = resolved;
  }
  if (!r) return '\u2014';
  if (r.min !== undefined && r.max !== undefined) return `${r.min}\u2013${r.max}`;
  if (r.max !== undefined) return `< ${r.max}`;
  if (r.min !== undefined) return `> ${r.min}`;
  return '\u2014';
}

function calculateAllScores(tests, userContext = null) {
  const cats = {};
  for (const test of tests) {
    const cat = OPTIMAL_RANGES[test.name]?.category || test.category;
    if (!cat) continue;
    if (!cats[cat]) cats[cat] = { testScores: [] };
    const score = scoreTest(test.name, test.value, userContext);
    cats[cat].testScores.push({ ...test, score, status: getStatus(score) });
  }

  const result = {};
  let totalScore = 0;
  let scoredCount = 0;
  for (const cat of CATEGORY_ORDER) {
    if (!cats[cat]) continue;
    const scored = cats[cat].testScores.filter((t) => t.score !== null);
    const avg = scored.length > 0
      ? Math.round(scored.reduce((s, t) => s + t.score, 0) / scored.length)
      : null;
    result[cat] = {
      score: avg,
      status: getStatus(avg),
      tests: cats[cat].testScores,
    };
    if (avg !== null) {
      totalScore += avg;
      scoredCount += 1;
    }
  }

  const overall = scoredCount > 0 ? Math.round(totalScore / scoredCount) : null;
  return { categories: result, overall, overallStatus: getStatus(overall) };
}

// ============================================================
// COMPONENT
// ============================================================

const AUTO_SAVE_DELAY_MS = 3000;

function LabResults() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromOnboarding = searchParams.get('from') === 'onboarding';
  const initialResultId = searchParams.get('resultId');
  const [view, setView] = useState(initialResultId ? 'loading_result' : 'upload');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [patientInfo, setPatientInfo] = useState({});
  const [tests, setTests] = useState([]);
  const [scores, setScores] = useState(null);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [savedResults, setSavedResults] = useState([]);
  const [saveStatus, setSaveStatus] = useState('');
  const fileInputRef = useRef(null);
  const [cyclePhase, setCyclePhase] = useState(null);
  const [userContext, setUserContext] = useState(null);
  const [currentResultId, setCurrentResultId] = useState(null);

  // Auto-save refs
  const autoSaveTimer = useRef(null);
  const testsRef = useRef(tests);
  const patientInfoRef = useRef(patientInfo);
  const fileNameRef = useRef(fileName);
  const scoresRef = useRef(scores);
  const currentResultIdRef = useRef(currentResultId);

  testsRef.current = tests;
  patientInfoRef.current = patientInfo;
  fileNameRef.current = fileName;
  scoresRef.current = scores;
  currentResultIdRef.current = currentResultId;

  useEffect(() => {
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, []);

  // ---- Auto-save logic ----

  const doAutoSave = useCallback(async () => {
    const t = testsRef.current;
    const pi = patientInfoRef.current;
    const fn = fileNameRef.current;
    const sc = scoresRef.current;
    const id = currentResultIdRef.current;
    if (!t || t.length === 0) return;

    setSaveStatus('saving');
    try {
      const payload = { file_name: fn || 'Lab Report', patient_info: pi, tests: t, scores: sc };
      if (id) {
        await apiService.labResults.updateResult(id, payload);
      } else {
        const res = await apiService.labResults.saveResults(payload);
        const newId = res.data?.id;
        if (newId) setCurrentResultId(newId);
      }
      setSaveStatus('saved');
    } catch (e) {
      setSaveStatus('error');
    }
  }, []);

  const scheduleAutoSave = useCallback(() => {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    setSaveStatus('');
    autoSaveTimer.current = setTimeout(doAutoSave, AUTO_SAVE_DELAY_MS);
  }, [doAutoSave]);

  // ---- Fetch saved results + questionnaire data on mount ----

  const fetchSavedResults = useCallback(async () => {
    try {
      const res = await apiService.labResults.getResults();
      setSavedResults(res.data || []);
    } catch {
      // silently fail
    }
  }, []);

  const fetchUserContext = useCallback(async () => {
    try {
      const res = await apiService.questionnaires.getDraft('intake');
      const draft = res.data?.draft;
      if (!draft?.responses || Object.keys(draft.responses).length === 0) return;
      const latest = draft.responses;
      const menopauseStatus = latest.menopause_status || null;
      let age = null;
      if (latest.date_of_birth) {
        const parts = latest.date_of_birth.split('/');
        if (parts.length === 3) {
          const dob = new Date(parts[2], parts[0] - 1, parts[1]);
          const today = new Date();
          age = today.getFullYear() - dob.getFullYear();
          if (today.getMonth() < dob.getMonth() ||
              (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
            age--;
          }
        }
      }
      setUserContext({ menopauseStatus, cyclePhase: null, age });
    } catch {
      // silently fail
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load a specific result by ID (for deep-linking from dashboard)
  const loadResultById = useCallback(async (resultId) => {
    setView('loading_result');
    setError('');
    try {
      const res = await apiService.labResults.getResultDetail(resultId);
      const data = res.data;
      setPatientInfo(data.patient_info || {});
      setTests(data.tests || []);
      setFileName(data.file_name || '');
      setCurrentResultId(data.id);
      if (data.scores) {
        setScores(data.scores);
        setView('results');
      } else {
        setView('form');
      }
    } catch {
      setError('Failed to load lab result.');
      setView('upload');
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchSavedResults();
      fetchUserContext();
      if (initialResultId) {
        loadResultById(parseInt(initialResultId, 10));
      }
    }
  }, [user, fetchSavedResults, fetchUserContext, initialResultId, loadResultById]);

  useEffect(() => {
    if (userContext) {
      setUserContext((prev) => prev ? { ...prev, cyclePhase } : prev);
    }
  }, [cyclePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Upload handlers ----

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
      setFileName(f.name);
      setError('');
    } else {
      setError('Please select a PDF file.');
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
      setFileName(f.name);
      setError('');
    } else {
      setError('Please drop a PDF file.');
    }
  };

  const handleExtract = async () => {
    if (!file) return;
    setView('extracting');
    setError('');
    try {
      const res = await apiService.labResults.extractLabData(file);
      const data = res.data;
      setPatientInfo(data.patient_info || {});
      setTests(data.tests || []);
      setCurrentResultId(null);
      setScores(null);
      setSaveStatus('');
      setView('form');
      // Auto-save immediately after extraction
      setTimeout(async () => {
        try {
          const saveRes = await apiService.labResults.saveResults({
            file_name: file.name || 'Lab Report',
            patient_info: data.patient_info || {},
            tests: data.tests || [],
            scores: null,
          });
          if (saveRes.data?.id) setCurrentResultId(saveRes.data.id);
          setSaveStatus('saved');
        } catch (e) {
          // first save failed, will retry on next auto-save
        }
      }, 100);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to extract lab data. Please try again.';
      setError(detail);
      setView('upload');
    }
  };

  // ---- Form handlers ----

  const handleTestChange = (idx, field, rawValue) => {
    setTests((prev) => {
      const updated = [...prev];
      let value = rawValue;
      if (field === 'value') {
        value = rawValue === '' ? null : parseFloat(rawValue);
        if (rawValue !== '' && isNaN(value)) value = null;
      }
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
    scheduleAutoSave();
  };

  const handlePatientInfoChange = (field, value) => {
    setPatientInfo((p) => ({ ...p, [field]: value }));
    scheduleAutoSave();
  };

  const handleAddTest = (category) => {
    setTests((prev) => [
      ...prev,
      { name: '', value: null, unit: '', standard_range: '', category },
    ]);
  };

  const handleRemoveTest = (idx) => {
    setTests((prev) => prev.filter((_, i) => i !== idx));
    scheduleAutoSave();
  };

  const handleCalculateScores = () => {
    const result = calculateAllScores(tests, userContext);
    setScores(result);
    setView('results');
    // Auto-save with scores
    const id = currentResultIdRef.current;
    const payload = { file_name: fileName || 'Lab Report', patient_info: patientInfo, tests, scores: result };
    if (id) {
      apiService.labResults.updateResult(id, payload).then(() => setSaveStatus('saved')).catch(() => setSaveStatus('error'));
    } else {
      apiService.labResults.saveResults(payload).then((res) => {
        if (res.data?.id) setCurrentResultId(res.data.id);
        setSaveStatus('saved');
      }).catch(() => setSaveStatus('error'));
    }
  };

  const handleReset = () => {
    setView('upload');
    setFile(null);
    setFileName('');
    setPatientInfo({});
    setTests([]);
    setScores(null);
    setError('');
    setSaveStatus('');
    setCurrentResultId(null);
    // Clear resultId from URL
    setSearchParams({});
    if (fileInputRef.current) fileInputRef.current.value = '';
    fetchSavedResults();
  };

  // ---- Delete handler ----

  const handleDelete = async (resultId) => {
    if (!window.confirm('Delete this lab result? This cannot be undone.')) return;
    try {
      await apiService.labResults.deleteResult(resultId);
      setSavedResults((prev) => prev.filter((r) => r.id !== resultId));
      // If we just deleted the currently loaded result, reset
      if (currentResultId === resultId) {
        handleReset();
      }
    } catch {
      setError('Failed to delete result.');
    }
  };

  // ---- Load saved result ----

  const handleLoadSaved = async (resultId) => {
    setView('extracting');
    setError('');
    try {
      const res = await apiService.labResults.getResultDetail(resultId);
      const data = res.data;
      setPatientInfo(data.patient_info || {});
      setTests(data.tests || []);
      setFileName(data.file_name || '');
      setCurrentResultId(data.id);
      if (data.scores) {
        setScores(data.scores);
        setView('results');
      } else {
        setView('form');
      }
    } catch {
      setError('Failed to load saved result.');
      setView('upload');
    }
  };

  // ---- Group tests by category for the form ----

  const groupedTests = () => {
    const groups = {};
    tests.forEach((test, idx) => {
      const cat = OPTIMAL_RANGES[test.name]?.category || test.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push({ ...test, _idx: idx });
    });
    return groups;
  };

  // Auto-save status label
  const autoSaveLabel =
    saveStatus === 'saving' ? 'Saving...' :
    saveStatus === 'saved' ? 'Saved' :
    saveStatus === 'error' ? 'Save failed' : null;

  // ---- Render: Upload view ----

  const renderUpload = () => (
    <>
      <h1>Lab Results</h1>
      <p>Upload your blood test PDF and get a personalized health risk assessment based on women's-health-optimized ranges.</p>

      <div className="card upload-card">
        <div
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="drop-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d6336c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="12" y2="12" />
              <line x1="15" y1="15" x2="12" y2="12" />
            </svg>
          </div>
          <p className="drop-text">Drag & drop your lab test PDF here</p>
          <p className="drop-subtext">or click to browse files</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {file && (
          <div className="selected-file">
            <span className="file-name">{file.name}</span>
            <span className="file-size">({(file.size / 1024).toFixed(0)} KB)</span>
          </div>
        )}

        {error && <div className="error-msg">{error}</div>}

        <button
          className="btn btn-primary extract-btn"
          onClick={handleExtract}
          disabled={!file}
        >
          Extract Lab Data
        </button>
      </div>

      {/* Past Results */}
      {savedResults.length > 0 && (
        <div className="card past-results-card">
          <h3>Past Results</h3>
          <div className="past-results-list">
            {savedResults.map((r) => (
              <div key={r.id} className="past-result-row">
                <button
                  className="past-result-item"
                  onClick={() => handleLoadSaved(r.id)}
                >
                  <div className="past-result-info">
                    <span className="past-result-name">
                      {r.patient_name || r.file_name || 'Lab Report'}
                    </span>
                    <span className="past-result-meta">
                      {r.lab_name && `${r.lab_name} \u00b7 `}
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                  {r.overall_score !== null && r.overall_score !== undefined && (
                    <span className={`score-badge badge-${getStatus(r.overall_score)}`}>
                      {r.overall_score}
                    </span>
                  )}
                </button>
                <button
                  className="delete-btn"
                  onClick={(e) => { e.stopPropagation(); handleDelete(r.id); }}
                  title="Delete result"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // ---- Render: Loading view ----

  const renderLoading = () => (
    <div className="loading-container">
      <div className="spinner" />
      <h2>Analyzing your lab report...</h2>
      <p>This may take 15-30 seconds as we extract every test value.</p>
    </div>
  );

  // ---- Render: Editable Form view ----

  const renderForm = () => {
    const groups = groupedTests();

    return (
      <>
        <div className="form-header">
          <h1>Review Extracted Results</h1>
          <div className="form-header-row">
            <p>Edit any values that were misread, then calculate your risk scores.</p>
            {autoSaveLabel && (
              <span className="auto-save-label" style={{ color: saveStatus === 'error' ? '#e03131' : '#868e96' }}>
                {autoSaveLabel}
              </span>
            )}
          </div>
        </div>

        {/* Patient info */}
        <div className="card patient-info-card">
          <h3>Patient Information</h3>
          <div className="patient-fields">
            {['name', 'date_of_birth', 'test_date', 'lab_provider'].map((field) => (
              <div key={field} className="patient-field">
                <label>{field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</label>
                <input
                  type="text"
                  value={patientInfo[field] || ''}
                  onChange={(e) => handlePatientInfoChange(field, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Cycle phase selector for premenopausal users with hormone tests */}
        {userContext?.menopauseStatus === 'premenopausal' &&
         tests.some((t) => HORMONE_RANGES[t.name] && t.name !== 'AMH') && (
          <div className="card patient-info-card">
            <h3>Menstrual Cycle Phase</h3>
            <p style={{ margin: '0 0 10px', fontSize: '0.9em', color: '#666' }}>
              What phase were you in when this blood was drawn? This determines hormone reference ranges.
            </p>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { key: 'follicular', label: 'Follicular' },
                { key: 'midcycle', label: 'Mid-cycle / Ovulation' },
                { key: 'luteal', label: 'Luteal' },
                { key: null, label: "I don't know" },
              ].map((opt) => (
                <button
                  key={opt.key || 'unknown'}
                  className={`btn btn-small ${cyclePhase === opt.key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCyclePhase(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tests grouped by category */}
        {CATEGORY_ORDER.map((cat) => {
          const catTests = groups[cat];
          if (!catTests || catTests.length === 0) return null;
          const info = CATEGORY_INFO[cat] || { name: cat };

          return (
            <div key={cat} className="card category-card">
              <h3>{info.name}</h3>
              <div className="tests-table">
                <div className="tests-header">
                  <span className="col-name">Test</span>
                  <span className="col-value">Value</span>
                  <span className="col-unit">Unit</span>
                  <span className="col-ref">Lab Range</span>
                  <span className="col-optimal">Optimal Range</span>
                  <span className="col-status">Status</span>
                  <span className="col-action"></span>
                </div>
                {catTests.map((test) => {
                  const score = scoreTest(test.name, test.value, userContext);
                  const status = getStatus(score);
                  return (
                    <div key={test._idx} className="test-row">
                      <input
                        className="col-name"
                        value={test.name}
                        onChange={(e) => handleTestChange(test._idx, 'name', e.target.value)}
                      />
                      <input
                        className="col-value"
                        type="number"
                        step="any"
                        value={test.value ?? ''}
                        onChange={(e) => handleTestChange(test._idx, 'value', e.target.value)}
                      />
                      <input
                        className="col-unit"
                        value={test.unit || ''}
                        onChange={(e) => handleTestChange(test._idx, 'unit', e.target.value)}
                      />
                      <span className="col-ref">{test.standard_range || '\u2014'}</span>
                      <span className="col-optimal">{getOptimalDisplay(test.name, userContext)}</span>
                      <span className={`col-status badge badge-${status}`}>
                        {status === 'unknown' ? '\u2014' : status}
                      </span>
                      <button
                        className="remove-btn"
                        onClick={() => handleRemoveTest(test._idx)}
                        title="Remove test"
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                className="btn btn-small btn-add"
                onClick={() => handleAddTest(cat)}
              >
                + Add Test
              </button>
            </div>
          );
        })}

        {/* Uncategorized tests */}
        {groups['other'] && groups['other'].length > 0 && (
          <div className="card category-card">
            <h3>Other Tests</h3>
            <div className="tests-table">
              <div className="tests-header">
                <span className="col-name">Test</span>
                <span className="col-value">Value</span>
                <span className="col-unit">Unit</span>
                <span className="col-ref">Lab Range</span>
                <span className="col-optimal">Optimal Range</span>
                <span className="col-status">Status</span>
                <span className="col-action"></span>
              </div>
              {groups['other'].map((test) => (
                <div key={test._idx} className="test-row">
                  <input
                    className="col-name"
                    value={test.name}
                    onChange={(e) => handleTestChange(test._idx, 'name', e.target.value)}
                  />
                  <input
                    className="col-value"
                    type="number"
                    step="any"
                    value={test.value ?? ''}
                    onChange={(e) => handleTestChange(test._idx, 'value', e.target.value)}
                  />
                  <input
                    className="col-unit"
                    value={test.unit || ''}
                    onChange={(e) => handleTestChange(test._idx, 'unit', e.target.value)}
                  />
                  <span className="col-ref">{test.standard_range || '\u2014'}</span>
                  <span className="col-optimal">{getOptimalDisplay(test.name, userContext)}</span>
                  <span className="col-status badge badge-unknown">{'\u2014'}</span>
                  <button
                    className="remove-btn"
                    onClick={() => handleRemoveTest(test._idx)}
                    title="Remove test"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="form-actions-bar">
          <button className="btn btn-secondary" onClick={handleReset}>
            Upload New PDF
          </button>
          <button className="btn btn-primary" onClick={handleCalculateScores}>
            Calculate Risk Scores
          </button>
        </div>
      </>
    );
  };

  // ---- Render: Results / dashboard view ----

  const renderResults = () => {
    if (!scores) return null;
    const { categories, overall, overallStatus } = scores;

    return (
      <>
        {/* Overall score header */}
        <div className="results-header">
          <div className={`overall-score score-${overallStatus}`}>
            <svg viewBox="0 0 120 120" className="score-ring">
              <circle cx="60" cy="60" r="54" fill="none" stroke="#e5e7eb" strokeWidth="8" />
              <circle
                cx="60" cy="60" r="54"
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(overall / 100) * 339.3} 339.3`}
                transform="rotate(-90 60 60)"
                className="score-ring-fill"
              />
            </svg>
            <div className="score-number">{overall ?? '\u2014'}</div>
            <div className="score-label">Overall Health Score</div>
          </div>
          <div className="results-summary">
            <h1>Risk Assessment</h1>
            {patientInfo.name && <p><strong>Patient:</strong> {patientInfo.name}</p>}
            {patientInfo.test_date && <p><strong>Test Date:</strong> {patientInfo.test_date}</p>}
            {patientInfo.lab_provider && <p><strong>Lab:</strong> {patientInfo.lab_provider}</p>}
            <p><strong>Tests Analyzed:</strong> {tests.filter((t) => t.value !== null).length}</p>
            {autoSaveLabel && (
              <p style={{ fontSize: '0.85em', color: saveStatus === 'error' ? '#e03131' : '#868e96' }}>
                {autoSaveLabel}
              </p>
            )}
          </div>
        </div>

        {/* Category cards */}
        <div className="category-grid">
          {CATEGORY_ORDER.map((cat) => {
            const data = categories[cat];
            if (!data) return null;
            const info = CATEGORY_INFO[cat] || { name: cat };

            return (
              <div key={cat} className="card result-card">
                <div className="result-card-header">
                  <h3>{info.name}</h3>
                  {data.score !== null && (
                    <span className={`score-badge badge-${data.status}`}>
                      {data.score}
                    </span>
                  )}
                </div>

                {data.score !== null && (
                  <div className="score-bar-container">
                    <div
                      className={`score-bar bar-${data.status}`}
                      style={{ width: `${data.score}%` }}
                    />
                  </div>
                )}

                <div className="result-tests">
                  {data.tests.map((t, i) => (
                    <div key={i} className="result-test-row">
                      <span className="rt-name">{t.name}</span>
                      <span className="rt-value">
                        {t.value !== null ? t.value : '\u2014'}
                        {t.unit ? ` ${t.unit}` : ''}
                      </span>
                      <span className={`rt-status badge badge-${t.status}`}>
                        {t.status === 'unknown' ? '\u2014' : t.status}
                      </span>
                    </div>
                  ))}
                </div>

                {cat === 'hormones' && userContext && (
                  <div className="context-note" style={{ fontStyle: 'italic' }}>
                    <strong>Ranges based on:</strong>{' '}
                    {userContext.menopauseStatus === 'postmenopausal' || userContext.menopauseStatus === 'perimenopausal'
                      ? userContext.menopauseStatus
                      : userContext.menopauseStatus === 'premenopausal' && userContext.cyclePhase
                        ? `premenopausal \u2014 ${userContext.cyclePhase === 'midcycle' ? 'mid-cycle' : userContext.cyclePhase} phase`
                        : 'no cycle phase selected'}
                    {userContext.age != null ? `, age ${userContext.age}` : ''}
                  </div>
                )}

                {info.context && (
                  <div className="context-note">
                    <strong>Women's Health Note:</strong> {info.context}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="card disclaimer-card">
          <strong>Medical Disclaimer:</strong> This assessment uses women's-health-optimized
          ranges from Healthstorm research for educational purposes. It is not a medical
          diagnosis. Always consult your healthcare provider for interpretation of lab results
          and health decisions.
        </div>

        <div className="form-actions-bar">
          <button className="btn btn-secondary" onClick={() => setView('form')}>
            Back to Edit
          </button>
          <button className="btn btn-primary" onClick={handleReset}>
            Analyze Another Report
          </button>
        </div>
      </>
    );
  };

  // ---- Auth gate ----

  if (!user) {
    return (
      <div className="page lab-results-page">
        <h1>Lab Results</h1>
        <p>Please log in to access the lab results analyzer.</p>
        <Link to="/login" className="btn btn-primary">Go to Login</Link>
      </div>
    );
  }

  // ---- Main render ----

  return (
    <div className="page lab-results-page">
      {fromOnboarding && (
        <div style={{ background: '#fff0f6', border: '1px solid #fcc2d7', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14 }}>You're setting up your account.</span>
          <Link to="/onboarding" style={{ color: '#d6336c', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>{'\u2190'} Back to Setup</Link>
        </div>
      )}
      {view === 'upload' && renderUpload()}
      {(view === 'extracting' || view === 'loading_result') && renderLoading()}
      {view === 'form' && renderForm()}
      {view === 'results' && renderResults()}
    </div>
  );
}

export default LabResults;
